import {BaseView} from "@nolimitcity/slot-game/bin/core/base/BaseView";
import {IResizeData, StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {IWinRatioConfig} from "@nolimitcity/slot-game/bin/core/winpresentation/WinRatio";
import {Timeline, TimelineLite, TimelineMax, TweenLite} from "gsap";
import {FITHGameConfig} from "../../FITHGameConfig";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {FITHGameModuleConfig} from "../../FITHGameModuleConfig";
import {TimelineSprite} from "@nolimitcity/slot-game/bin/core/animation/TimelineSprite";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {ScalableLayer} from "@nolimitcity/slot-game/bin/core/stage/ScalableLayer";
import {SpineTween} from "@nolimitcity/slot-game/bin/core/animation/SpineTween";
import {FITHSoundManager} from "../../sound/FITHSoundManager";
import {FITHSoundConfig} from "../../FITHSoundConfig";

export class FITHBigWinView extends BaseView {
    private _centre: number = FITHGameConfig.instance.REEL_AREA_WIDTH / 2;
    private _bigWinLayer: ScalableLayer;
    private _explode: TimelineSprite;
    private _explodeLv5Tween: TweenLite;
    private _dimmer: PIXI.Graphics;
    private _fireSmoke: TimelineSprite;
    private _bigWinLevelCharacter: PIXI.Sprite;
    private _characterLevel5: PIXI.spine.Spine;
    private _characterTimeline: TimelineMax;
    private _characterSmokeScreen: TimelineSprite;
    private _bigWinTextLabel: PIXI.Sprite;
    private _labelI: PIXI.Sprite;
    private _labelWant: PIXI.Sprite;
    private _labelDot1: PIXI.Sprite;
    private _labelDot2: PIXI.Sprite;
    private _labelMore: PIXI.Sprite;
    private _labelMoreScreen: PIXI.Sprite;
    private _shakeTween: TweenLite;
    private _fire: TimelineSprite;
    private _redFlash: PIXI.Graphics;
    private _currentLevel: string = "";

    public getBigWinLevelAnimation(winRatioConfig: IWinRatioConfig, level: number): Timeline {
        const timeline: TimelineLite = new TimelineLite();

        this._currentLevel = level + "";
        timeline.add(() => {
            // #265
            if (level === 1) {
                FITHSoundManager.fadeForBigWin(0, 500);
            } else {
                FITHSoundManager.stopSoundEffect(FITHSoundConfig.instance.BIG_WIN_LEVEL + (level - 1));
            }
            FITHSoundManager.playBigWinLevel(level);

            this.updateBigWinLevelAnimation(level, winRatioConfig.duration);
            FITHGameModuleConfig.coinsFountain.startFountain();
        });

        timeline.add(() => {
        }, winRatioConfig.duration);

        return timeline;
    }

    public playEndingAnimation(finalLevel: number, isPlayingForOutro: boolean): void {
        // #265
        FITHSoundManager.playBigWinEnds();
        FITHSoundManager.stopSoundEffect(FITHSoundConfig.instance.BIG_WIN_LEVEL + finalLevel);

        // #277, #289
        if (isPlayingForOutro) {
            FITHSoundManager.endForBigWinInFS(0, 1);
        } else {
            FITHSoundManager.endForBigWin(1, 1500);
        }

        this._currentLevel = "";

        this.stopItems();

        TweenLite.fromTo(this._dimmer, 0.2, {alpha: 0.6}, {alpha: 0, delay: 0.8});

        TweenLite.killTweensOf(this._bigWinTextLabel, true);
        TweenLite.to(this._bigWinTextLabel, 0.2, {alpha: 0, delay: 0.8});
        if (finalLevel === 5) {
            TweenLite.fromTo(this._characterLevel5, 0.2, {alpha: 1}, {
                alpha: 0,
                delay: 0.8
            });
        } else {
            TweenLite.fromTo(this._bigWinLevelCharacter, 0.2, {alpha: 1}, {alpha: 0, delay: 0.8});
        }

        TweenLite.delayedCall(1, () => {
            this._dimmer.visible = false;
            this._bigWinLevelCharacter.visible = false;
            this._characterLevel5.visible = false;
            this._characterLevel5.state.clearTracks();
            this._bigWinTextLabel.visible = false;
            this._redFlash.visible = false;
        });
    }

    protected onResize(resizeData: IResizeData): void {
        super.onResize && super.onResize(resizeData);
        if (this._fireSmoke.visible) {
            this.setFireProps();
            this.updateDimmer();
        }
    }

    protected initAnimations(): void {
        this._bigWinLayer = StageManager.getLayer(FITHGameConfig.instance.LAYERS.BIG_WIN.name);

        this._dimmer = new PIXI.Graphics();
        this._dimmer.visible = false;
        this._bigWinLayer.addChild(this._dimmer);
        this.createFireSmoke();
        this.createOtherCharacters();
        this.createBigWinCharacterLevel5();
        this.createCharacterSmokeScreen();
        this.createLv5ExtraLabels();
        this.createLabel();
        this.createBigWinFire();
        this.createRedFlash();
        this.createBigWinExplode();

        FITHGameConfig.instance.STAGE_INIT_Y = StageManager.stage.y;
    }

    private createFireSmoke(): void {
        this._fireSmoke = new TimelineSprite(GameResources.getTextures("bigWinFireSmoke"));
        this._fireSmoke.hide();
        this._fireSmoke.anchor.set(0.5);
        this._fireSmoke.position.set(this._centre);
        this._fireSmoke.blendMode = PIXI.BLEND_MODES.SCREEN;
        this._bigWinLayer.addChild(this._fireSmoke);
    }

    private createOtherCharacters(): void {
        this._bigWinLevelCharacter = new PIXI.Sprite(PIXI.Texture.EMPTY);
        this._bigWinLevelCharacter.anchor.set(0.5);
        this._bigWinLevelCharacter.position.set(this._centre);
        this._bigWinLevelCharacter.visible = false;
        this._bigWinLayer.addChild(this._bigWinLevelCharacter);
    }

    private createBigWinCharacterLevel5(): void {
        this._characterLevel5 = new PIXI.spine.Spine(GameResources.getSpineAsset("BigWinChar"));
        this._characterLevel5.visible = false;
        this._characterLevel5.position.set(this._centre);
        this._bigWinLayer.addChild(this._characterLevel5);
    }

    // #135
    private createCharacterSmokeScreen(): void {
        this._characterSmokeScreen = new TimelineSprite(GameResources.getTextures("bigWinFireSmoke"));
        this._characterSmokeScreen.hide();
        this._characterSmokeScreen.anchor.set(0.5);
        this._characterSmokeScreen.position.set(this._centre, this._centre + 150);
        this._characterSmokeScreen.scale.set(1.6, 0.5);
        this._characterSmokeScreen.blendMode = PIXI.BLEND_MODES.SCREEN;
        this._bigWinLayer.addChild(this._characterSmokeScreen);
    }

    // #138
    private createLv5ExtraLabels(): void {
        const yPos: number = 130;

        this._labelI = new PIXI.Sprite(GameResources.getTextures("bigWinTitle5I")[0]);
        this._labelI.visible = false;
        this._labelI.anchor.set(0.5);
        this._labelI.position.set(80, yPos);

        this._labelWant = new PIXI.Sprite(GameResources.getTextures("bigWinTitle5Want")[0]);
        this._labelWant.visible = false;
        this._labelWant.anchor.set(0.5);
        this._labelWant.position.set(228, yPos);

        this._labelDot1 = new PIXI.Sprite(GameResources.getTextures("bigWinTitle5Dot")[0]);
        this._labelDot1.visible = false;
        this._labelDot1.anchor.set(0.5);
        this._labelDot1.position.set(385, yPos + 25);

        this._labelDot2 = new PIXI.Sprite(GameResources.getTextures("bigWinTitle5Dot")[0]);
        this._labelDot2.visible = false;
        this._labelDot2.anchor.set(0.5);
        this._labelDot2.position.set(435, yPos + 25);

        this._labelMore = new PIXI.Sprite(GameResources.getTextures("bigWinTitle5More")[0]);
        this._labelMore.visible = false;
        this._labelMore.anchor.set(0.5);
        this._labelMore.position.set(510, yPos);

        this._labelMoreScreen = new PIXI.Sprite(GameResources.getTextures("bigWinTitle5More")[0]);
        this._labelMoreScreen.visible = false;
        this._labelMoreScreen.anchor.set(0.5);
        this._labelMoreScreen.position.set(510, yPos);
        this._labelMoreScreen.blendMode = PIXI.BLEND_MODES.SCREEN;

        this._bigWinLayer.addChild(this._labelI, this._labelWant, this._labelDot1, this._labelDot2, this._labelMore, this._labelMoreScreen);
    }

    private createLabel(): void {
        this._bigWinTextLabel = new PIXI.Sprite(PIXI.Texture.EMPTY);
        this._bigWinTextLabel.visible = false;
        this._bigWinTextLabel.anchor.set(0.5);
        this._bigWinTextLabel.position.set(this._centre, 130);

        this._bigWinLayer.addChild(this._bigWinTextLabel);
    }

    private createBigWinFire(): void {
        this._fire = new TimelineSprite(GameResources.getTextures("bigWinFire"));
        this._fire.hide();
        this._fire.anchor.set(0.5);
        this._fire.blendMode = PIXI.BLEND_MODES.ADD;
        this._fire.position.set(this._centre, 0);
        this._fire.scale.x = 2;
        this._bigWinLayer.addChild(this._fire);
    }

    private createRedFlash(): void {
        this._redFlash = new PIXI.Graphics();
        this._redFlash.visible = false;
        this._redFlash.beginFill(0xff0000);
        this._redFlash.drawRect(0, 0, 1680, 1680);
        this._redFlash.endFill();
        this._redFlash.position.set(-this._centre);
        this._bigWinLayer.addChild(this._redFlash);
    }

    private createBigWinExplode(): void {
        this._explode = new TimelineSprite(GameResources.getTextures("explodeEff"));
        this._explode.hide();
        this._explode.position.set(this._centre);
        this._explode.anchor.set(0.5);
        this._explode.scale.set(2);
        this._explode.blendMode = PIXI.BLEND_MODES.SCREEN;
        StageManager.getLayer(FITHGameConfig.instance.LAYERS.BIG_WIN_EXPLODE.name).addChild(this._explode);
    }

    private updateBigWinLevelAnimation(level: number, duration: number): void {
        let fireSmokeScale: number = 1;
        if (level === 2) {
            fireSmokeScale = 1.5;
        } else if (level > 2) {
            fireSmokeScale = 2;
        }

        if (level === 5) {
            this.playCharacterAnimationLv5();
        } else {
            this.playCharacterAnimation(level, (duration * 4));
        }
        this._characterSmokeScreen.playLoop();
        this._characterSmokeScreen.show();

        this._redFlash.visible = true;
        TweenLite.fromTo(this._redFlash, 5 / 30, {alpha: 0.5}, {alpha: 0});
        TweenLite.to(StageManager.stage, 0.4, {y: "+=" + 30, ease: "shakeBounce"});

        if (!this._fireSmoke.visible) {
            this.updateDimmer();
            this._dimmer.alpha = 0;
            this._dimmer.visible = true;
            TweenLite.fromTo(this._dimmer, 5 / 30, {alpha: 0}, {alpha: 0.6});

            this._fireSmoke.playLoop();
            this._fireSmoke.show();
        }
        this._fireSmoke.scale.set(fireSmokeScale);

        if (level === 4) {
            FITHGameModuleConfig.coinsFountain.startSmoke();
            this._explode.getAnimationAutoShowHide(true, true, undefined, undefined, () => FITHGameModuleConfig.coinsFountain.stopSmoke());
        }

        this.playLabelAnimation(level);
        this.playBigWinFire();
    }

    private updateDimmer(): void {
        this._dimmer.clear();
        this._dimmer.beginFill(0x000000);
        this._dimmer.drawRect(-280, -280, NolimitApplication.screenBounds.width + 280, NolimitApplication.screenBounds.height + 560);
        this._dimmer.endFill();
        this._dimmer.alpha = 0.6;
    }

    // changed in #138 and then in #160
    private playLabelAnimation(level: number): void {
        const duration: number = 0.5;
        TweenLite.killTweensOf(this._bigWinTextLabel, true);
        this._bigWinTextLabel.texture = GameResources.getTextures("bigWinTitle" + level)[0];
        this._bigWinTextLabel.alpha = 0;

        if (level === 5) {
            this._bigWinTextLabel.visible = false;
            this.playLabelAnimationLv5A();
        } else {
            this._bigWinTextLabel.visible = true;
            TweenLite.to(this._bigWinTextLabel, duration, {alpha: 1});
            TweenLite.to(this._bigWinTextLabel, (duration * 4), {alpha: 0, delay: (2 * duration)});
        }
    }

    // #138
    private playLabelAnimationLv5A(): void {
        const dur: number = 5 / 30,
            othersOriginalY: number = this._labelI.y,
            dotOriginalY: number = this._labelDot1.y;

        this._labelI.visible = true;
        this._labelWant.visible = true;
        this._labelDot1.visible = true;
        this._labelDot2.visible = true;

        this._labelI.alpha = 0;
        this._labelWant.alpha = 0;
        this._labelDot1.alpha = 0;
        this._labelDot2.alpha = 0;

        this._labelI.y = othersOriginalY + 20;
        this._labelWant.y = othersOriginalY + 20;
        this._labelDot1.y = dotOriginalY + 20;
        this._labelDot2.y = dotOriginalY + 20;

        TweenLite.to(this._labelI, dur, {alpha: 1});
        TweenLite.to(this._labelI, dur, {y: othersOriginalY});

        TweenLite.to(this._labelWant, dur, {alpha: 1, delay: dur});
        TweenLite.to(this._labelWant, dur, {y: othersOriginalY, delay: dur});

        TweenLite.to(this._labelDot1, dur, {alpha: 1, delay: (2 * dur)});
        TweenLite.to(this._labelDot1, dur, {y: dotOriginalY, delay: (2 * dur)});

        TweenLite.to(this._labelDot2, dur, {alpha: 1, delay: (3 * dur)});
        TweenLite.to(this._labelDot2, dur, {y: dotOriginalY, delay: (3 * dur)});
    }

    // #138
    private playLabelAnimationLv5B(): void {
        const onDur: number = 6 / 30,
            offDur: number = 9 / 30;

        this._labelDot1.visible = false;
        this._labelDot2.visible = false;

        this._labelMore.visible = true;

        this._labelMoreScreen.visible = true;
        this._labelMoreScreen.alpha = 0;
        this._labelMoreScreen.scale.set(1.3);

        TweenLite.to(this._labelMoreScreen.scale, onDur, {x: 1, y: 1});
        TweenLite.to(this._labelMoreScreen, onDur, {alpha: 1});
        TweenLite.to(this._labelMoreScreen, offDur, {alpha: 0, delay: onDur});

        this._shakeTween = TweenLite.delayedCall((onDur + offDur), () => {
            this._bigWinTextLabel.alpha = 1;
            this._bigWinTextLabel.visible = true;

            this._labelI.visible = false;
            this._labelWant.visible = false;
            this._labelMore.visible = false;
            this._labelMoreScreen.visible = false;

            TweenLite.to(this._bigWinTextLabel, 2, {alpha: 0, delay: 0.5});
        });
    }

    private playCharacterAnimation(level: number, charDuration: number): void {
        const fromScale: number = 0.85,
            toScale: number = 1.2;
        let duration: number = 0.2;

        if (this._characterTimeline) {
            this._characterTimeline.clear(true);
            this._characterTimeline.kill();
        }
        this._characterTimeline = new TimelineMax();
        this._bigWinLevelCharacter.texture = GameResources.getTextures("bigWinChar" + level)[0];
        this._bigWinLevelCharacter.scale.set(fromScale);
        this._bigWinLevelCharacter.alpha = 0;
        this._bigWinLevelCharacter.visible = true;

        if (level === 1) {
            duration = 0.5;
        }

        this._characterTimeline.to(this._bigWinLevelCharacter, duration, {alpha: 1});
        this._characterTimeline.to(this._bigWinLevelCharacter.scale, charDuration, {
            x: toScale,
            y: toScale
        });
    }

    private playCharacterAnimationLv5(): void {
        this.stopCharacterTimeline();
        this._bigWinLevelCharacter.visible = false;

        this._characterLevel5.state.clearTracks();
        this._characterLevel5.skeleton.setSlotsToSetupPose();
        this._characterLevel5.state.setEmptyAnimation(0, 0);
        this._characterLevel5.updateTransform();
        this._characterLevel5.visible = true;
        this._characterLevel5.alpha = 1;
        new SpineTween(this._characterLevel5, 0, "Lv5");

        this._explodeLv5Tween = TweenLite.delayedCall(2, () => {
            this.playLabelAnimationLv5B();
            TweenLite.killTweensOf(StageManager.stage);
            TweenLite.to(StageManager.stage, 0.4, {y: "+=" + 30, ease: "shakeBounce"});
            FITHGameModuleConfig.coinsFountain.startSmoke();
            this._explode.getAnimationAutoShowHide(true, true, undefined, undefined, () => FITHGameModuleConfig.coinsFountain.stopSmoke());
        });
    }

    private playBigWinFire(): void {
        this.setFireProps();
        this._fire.show();
        this._fire.alpha = 1;
        this._fire.playLoop();
    }

    private setFireProps(): void {
        if (this._currentLevel === "") {
            return;
        }

        let yOffset: number = 0,
            heightCalculator: number = 0;
        if (this._currentLevel === "1") {
            yOffset = 40;
            heightCalculator = 0.2;
        } else if (this._currentLevel === "2") {
            yOffset = 80;
            heightCalculator = 0.3;
        } else if (this._currentLevel === "3") {
            yOffset = 110;
            heightCalculator = 0.5;
        } else if (this._currentLevel === "4") {
            yOffset = 140;
            heightCalculator = 0.65;
        } else if (this._currentLevel === "5") {
            yOffset = 160;
            heightCalculator = 0.85;
        }
        this._fire.y = NolimitApplication.screenBounds.bottom - yOffset;
        this._fire.height = (NolimitApplication.screenBounds.height * heightCalculator);
    }

    private stopCharacterTimeline(): void {
        if (this._characterTimeline) {
            this._characterTimeline.clear(true);
            this._characterTimeline.pause();
            this._characterTimeline.kill();
        }
    }

    private stopItems(): void {
        this.stopCharacterTimeline();
        if (this._explodeLv5Tween) {
            this._explodeLv5Tween.pause();
            this._explodeLv5Tween.kill();
        }
        if (this._shakeTween) {
            this._shakeTween.pause();
            this._shakeTween.kill();
        }
        FITHGameModuleConfig.coinsFountain.stopFountain();
        this._fireSmoke.hide();
        this._fireSmoke.stopLoop();
        this._fire.hide();
        this._fire.stopLoop();
        this._characterSmokeScreen.hide();
        this._characterSmokeScreen.stopLoop();
        this._labelI.visible = false;
        this._labelWant.visible = false;
        this._labelDot1.visible = false;
        this._labelDot2.visible = false;
        this._labelMore.visible = false;
        this._labelMoreScreen.visible = false;

        // #225
        TweenLite.killTweensOf(StageManager.stage);
        StageManager.stage.y = FITHGameConfig.instance.STAGE_INIT_Y;
    }
}